import {
  createContext,
  useEffect,
  useState,
  useMemo,
  SetStateAction,
  useCallback,
  type JSX,
} from 'react';
import * as React from 'react';
import {
  apiLogout,
  apiUserGet,
  apiCheckAuthentication,
  apiGetUserExperiment,
} from 'lib/api';
import { User } from 'entities/user';
import {
  defaultUserPropertiesForUser,
  identifyUser,
  resetUser,
  trackEvent,
} from 'lib/analytics';
import { wipeSession } from 'lib/session';
import { setToSessionStorage } from 'lib/utils/set-to-session-storage';
import { EventType } from 'lib/events';
import { useQueryClient } from '@tanstack/react-query';

type UserProviderProps = {
  children: JSX.Element;
};

const defaultUser = {} as User;
const setDefaultUser = (() => {}) as React.Dispatch<
  SetStateAction<typeof defaultUser | null>
>;

export type SignInState =
  | 'newUser'
  | 'signingIn'
  | 'creatingAnAccount'
  | 'signedIn';
type AuthFormErrors = {
  emailError?: string;
  passwordError?: string;
};

type AuthenticationErrors = {
  register?: string;
  login?: string;
};

export const UserContext = createContext({
  userState: defaultUser,
  setUserState: setDefaultUser,
  isLoadingUser: true as boolean,
  loginEmail: '',
  loginPassword: '',
  signInState: 'newUser' as SignInState,
  setSignInState: (_mode: SignInState) => null as void,
  setLoginEmail: (_email: string) => null as void,
  setLoginPassword: (_password: string) => null as void,
  clearAuthFormErrors: () => null,

  authenticationFormErrors: {
    emailError: '',
    passwordErrors: '',
  } as AuthFormErrors,
  setAuthenticationFormErrors: (() => {}) as React.Dispatch<
    SetStateAction<AuthFormErrors>
  >,

  userAuthenticationErrors: {
    register: '',
    login: '',
  } as AuthenticationErrors,
  setUserAuthenticationErrors: (() => {}) as React.Dispatch<
    SetStateAction<AuthenticationErrors>
  >,

  checkUserExperiment: (_experimentName: string, _variation: string) =>
    false as boolean,
  getUserExperimentFromCore: (_experimentName: string): void => {},
  updateUserDataFromCore: async (): Promise<User | null> => null,
  loginMethod: null as string | null,
});

let persistedUserData;
if (typeof window !== 'undefined') {
  const data = localStorage.getItem('userState');
  if (data && data !== 'undefined') {
    persistedUserData = JSON.parse(data);
  }
}

export const UserProvider: React.FC<UserProviderProps> = ({
  children,
}: UserProviderProps) => {
  const [userState, setUserStateInternal] = useState(persistedUserData);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [isLoadingUser, setIsLoadingUser] = useState(true);
  const [loginMethod, setLoginMethod] = useState<string | null>(null);
  const queryClient = useQueryClient();

  //--------------------v3-------------------//
  const [authenticationFormErrors, setAuthenticationFormErrors] =
    useState<AuthFormErrors>({}); // for checkout v3
  const [userAuthenticationErrors, setUserAuthenticationErrors] =
    useState<AuthenticationErrors>({}); // for checkout v3
  //--------------------v3-------------------//
  const clearAuthFormErrors = () => {
    setAuthenticationFormErrors({});
    setUserAuthenticationErrors({});
  };

  const [signInState, setSignInStateInt] = useState<SignInState>(
    userState?.email ? 'signedIn' : 'newUser',
  );

  const setSignInState = (mode: SignInState) => {
    clearAuthFormErrors();
    setSignInStateInt(mode);
  };

  const updateUserDataFromCore = async (): Promise<User | null> => {
    const { status, data }: { status: number; data: User } = await apiUserGet();
    if (status === 200) {
      setUserStateInternal(data);
      return data;
    }
    return null;
  };

  const checkUserAuthorization = useCallback(async () => {
    setIsLoadingUser(true);
    try {
      const checkAuthentication = await apiCheckAuthentication();
      if (checkAuthentication.success) {
        setLoginMethod(checkAuthentication.loginMethod || null);
        const user = await apiUserGet();
        if (user.status === 200) {
          setUserStateInternal(user.data);
        } else {
          wipeSession();
          setLoginMethod(null);
        }
      } else {
        wipeSession();
        setUserStateInternal(null);
        setLoginMethod(null);
      }
    } catch {
      wipeSession();
      setUserStateInternal(null);
      setLoginMethod(null);
    } finally {
      setIsLoadingUser(false);
    }
  }, []);
  const logout = async () => {
    try {
      const response = await apiLogout();
      trackEvent('Logout');
      resetUser();
      wipeSession();
      // wipe out the chat session too
      (window as any).dgchat?.methods?.resetChat?.();
      localStorage.removeItem('userState');
      setUserStateInternal(null);
      setLoginMethod(null);

      // If the server returned a Shopify logout URL, redirect there to end the Shopify session.
      // Shopify's end_session_endpoint will redirect back to the homepage after ending the session.
      if (response?.data?.shopifyLogoutUrl) {
        setToSessionStorage(
          'pendingSnackbar',
          JSON.stringify({
            message: "You've been signed out",
            variant: 'success',
          }),
        );
        window.location.href = response.data.shopifyLogoutUrl;
      } else {
        window.location.href = '/';
      }
    } catch (error) {
      console.error('Logout failed:', error);
      setToSessionStorage(
        'pendingSnackbar',
        JSON.stringify({
          message: 'Sign out failed. Please try again.',
          variant: 'error',
        }),
      );
      // Even if logout API fails, redirect to homepage
      window.location.href = '/';
    }
  };

  useEffect(() => {
    const handleLogOut = () => {
      logout();
    };
    window.addEventListener(EventType.LOGOUT, handleLogOut);
    return () => {
      window.removeEventListener(EventType.LOGOUT, handleLogOut);
    };
  }, []);

  useEffect(() => {
    checkUserAuthorization();
  }, [checkUserAuthorization]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (userState && (userState.account || userState.isShopifyUser)) {
        delete userState.seedSessionToken;
        if (userState.account) {
          delete userState.account.seedSessionToken;
        }
        localStorage.setItem('userState', JSON.stringify(userState));

        identifyUser(
          userState.externalId,
          defaultUserPropertiesForUser(userState),
        );
      }
    }
  }, [userState]);

  const setUserState = (state) => {
    if (!state) {
      logout();
    } else if (state?.account || state?.isShopifyUser) {
      setUserStateInternal(state);
    }
  };

  const checkUserExperiment = (experimentName, variation) =>
    userState?.experiments?.[experimentName] === variation;

  const getUserExperimentFromCore = async (experimentName: string) => {
    // invoke the experiment using this endpoint
    // update the UserProvider which should include new experiments
    try {
      const experiment = await apiGetUserExperiment(experimentName);
      return experiment;
    } catch {
      return { error: 'Error fetching experiment' };
    }
  };

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
  }, [userState]);

  const memoizedUserState = useMemo(
    () => ({
      userState,
      setUserState,
      loginEmail,
      setLoginEmail,
      loginPassword,
      setLoginPassword,
      clearAuthFormErrors,
      authenticationFormErrors,
      setAuthenticationFormErrors,
      userAuthenticationErrors,
      setUserAuthenticationErrors,
      signInState,
      setSignInState,
      checkUserExperiment,
      getUserExperimentFromCore,
      updateUserDataFromCore,
      loginMethod,
      isLoadingUser,
    }),
    [
      userState,
      loginEmail,
      loginPassword,
      signInState,
      authenticationFormErrors,
      userAuthenticationErrors,
      loginMethod,
      isLoadingUser,
      checkUserExperiment,
      setSignInState,
    ],
  );

  // console.log('memoizedUserState', memoizedUserState);

  return (
    <UserContext.Provider value={memoizedUserState}>
      {children}
    </UserContext.Provider>
  );
};
